import { trimmedStringFromUnknown } from "../protocol/shared.js";
import * as z$1 from "zod/v4";
//#region src/schemas/http/space-mention-label.ts
const MULTI_SPACE_PATTERN = /\s+/g;
const SPACE_MENTION_LABEL_MAX_LENGTH = 128;
const SPACE_MENTION_LABEL_SOURCE_VALUES = [
	"profile_name",
	"mention_alias",
	"profile_name_with_short_id",
	"profile_id"
];
const spaceMentionLabelSourceSchema = z$1.enum(SPACE_MENTION_LABEL_SOURCE_VALUES);
const spaceMentionLabelSchema = z$1.preprocess(normalizeRawMentionLabelCandidate, z$1.string().min(1).max(128).refine((value) => !(value.includes("@") || value.includes("(") || value.includes(")")), "Space mention labels must not contain @ or parentheses"));
function resolveSpaceMentionLabels(identities) {
	const projected = [...identities].map(projectSpaceMentionLabelIdentity);
	const exactLabelCounts = buildExactLabelCounts(projected);
	return new Map(projected.map((identity) => {
		const uniqueProfileName = resolveUniqueExactLabel(identity.normalizedProfileName, exactLabelCounts);
		const uniqueMentionAlias = resolveUniqueExactLabel(identity.normalizedMentionAlias, exactLabelCounts);
		const uniqueProfileNameWithShortId = resolveUniqueExactLabel(identity.profileNameWithShortId, exactLabelCounts);
		const primaryLabelState = resolvePrimaryMentionLabel({
			mentionAlias: uniqueMentionAlias,
			profileId: identity.profileId,
			profileName: uniqueProfileName,
			profileNameWithShortId: uniqueProfileNameWithShortId
		});
		return [identity.profileId, {
			exactMentionLabels: buildExactMentionLabels({
				mentionAlias: uniqueMentionAlias,
				profileId: identity.profileId,
				profileName: uniqueProfileName,
				profileNameWithShortId: uniqueProfileNameWithShortId
			}),
			labelSource: primaryLabelState.labelSource,
			primaryMentionLabel: primaryLabelState.primaryMentionLabel
		}];
	}));
}
function resolveSingleSpaceMentionLabels(identity) {
	const profileId = normalizeRequiredProfileId(identity.profileId);
	const resolved = resolveSpaceMentionLabels([identity]).get(profileId);
	if (!resolved) throw new Error(`Missing resolved space mention labels for profile ${profileId}.`);
	return resolved;
}
function projectSpaceMentionLabelIdentity(identity) {
	const profileId = normalizeRequiredProfileId(identity.profileId);
	const normalizedProfileName = normalizeProjectableMentionLabel(identity.profileName);
	const normalizedMentionAlias = normalizeProjectableMentionLabel(identity.mentionAlias);
	const profileNameWithShortId = normalizedProfileName ? `${normalizedProfileName}-${buildProfileIdShort(profileId)}` : null;
	return {
		mentionAlias: normalizeOptionalText(identity.mentionAlias),
		normalizedMentionAlias,
		normalizedProfileName,
		profileId,
		profileNameWithShortId
	};
}
function normalizeRawMentionLabelCandidate(value) {
	const normalized = trimmedStringFromUnknown(value).replace(MULTI_SPACE_PATTERN, " ").trim();
	if (normalized.length === 0) return;
	return normalized;
}
function normalizeProjectableMentionLabel(value) {
	const parsed = spaceMentionLabelSchema.safeParse(value);
	return parsed.success ? parsed.data : null;
}
function buildExactLabelCounts(identities) {
	const counts = /* @__PURE__ */ new Map();
	for (const identity of identities) {
		addExactLabelCount(counts, identity.profileId);
		addExactLabelCount(counts, identity.normalizedProfileName);
		addExactLabelCount(counts, identity.normalizedMentionAlias);
		addExactLabelCount(counts, identity.profileNameWithShortId);
	}
	return counts;
}
function addExactLabelCount(counts, value) {
	if (!value) return;
	counts.set(value, (counts.get(value) ?? 0) + 1);
}
function resolveUniqueExactLabel(value, counts) {
	if (!value) return null;
	return counts.get(value) === 1 ? value : null;
}
function resolvePrimaryMentionLabel(input) {
	if (input.profileName) return {
		labelSource: "profile_name",
		primaryMentionLabel: input.profileName
	};
	if (input.mentionAlias) return {
		labelSource: "mention_alias",
		primaryMentionLabel: input.mentionAlias
	};
	if (input.profileNameWithShortId) return {
		labelSource: "profile_name_with_short_id",
		primaryMentionLabel: input.profileNameWithShortId
	};
	return {
		labelSource: "profile_id",
		primaryMentionLabel: input.profileId
	};
}
function buildExactMentionLabels(input) {
	const orderedCandidates = [
		input.profileName,
		input.mentionAlias,
		input.profileNameWithShortId,
		input.profileId
	];
	const seen = /* @__PURE__ */ new Set();
	const exactMentionLabels = [];
	for (const candidate of orderedCandidates) {
		if (!(candidate && !seen.has(candidate))) continue;
		seen.add(candidate);
		exactMentionLabels.push(candidate);
	}
	return exactMentionLabels;
}
function buildProfileIdShort(profileId) {
	if (profileId.startsWith("agt_")) return `agt_${profileId.slice(4, 9)}`;
	return profileId.slice(0, 5);
}
function normalizeRequiredProfileId(profileId) {
	const normalized = String(profileId ?? "").trim();
	if (normalized.length === 0) throw new Error("Space mention labels require a non-empty profileId.");
	return normalized;
}
function normalizeOptionalText(value) {
	const normalized = trimmedStringFromUnknown(value);
	return normalized.length > 0 ? normalized : null;
}
//#endregion
export { SPACE_MENTION_LABEL_MAX_LENGTH, SPACE_MENTION_LABEL_SOURCE_VALUES, resolveSingleSpaceMentionLabels, resolveSpaceMentionLabels, spaceMentionLabelSchema, spaceMentionLabelSourceSchema };

//# sourceMappingURL=space-mention-label.js.map